import React, { useMemo, useEffect, useState } from 'react';
import CountUp from "react-countup";
import { Link } from 'react-router-dom';
import { Card, CardBody, Col } from 'reactstrap';
import LogoSpinner from '../../Components/Common/LogoSpinner';

// Helper function to check if data is sample header metrics
const isSampleHeaderMetrics = (metrics) => {
    // If no metrics or all zero values, consider it sample data
    if (!metrics || Object.keys(metrics).length === 0) {
        return true;
    }
    
    // Check if all key metrics are zero or missing
    const keysToCheck = ['total_active_users', 'new_users_today', 'active_subscriptions', 'total_revenue', 'active_sessions'];
    const allZeroOrMissing = keysToCheck.every(key => !metrics[key] || metrics[key] === 0);
    
    return allZeroOrMissing;
};

const Widgets = ({ headerMetrics = {} }) => {
    // Add local state to store metrics from WebSocket
    const [localHeaderMetrics, setLocalHeaderMetrics] = useState(headerMetrics);
    const [loading, setLoading] = useState(true);
    const [dataReceived, setDataReceived] = useState(false);

    // Update local state when props change
    useEffect(() => {
        // Only update from props if we don't already have data from WebSocket
        // This prevents the default data from overriding the API data
        if (Object.keys(headerMetrics).length > 0) {
            console.log('Widgets - Updating from props:', headerMetrics);
            setLocalHeaderMetrics(prev => {
                // Only use props data if we don't already have data
                if (Object.keys(prev).length === 0) {
                    return headerMetrics;
                }
                // Otherwise, keep our existing data
                return prev;
            });
        }
    }, [headerMetrics]);

    // Debug log to check incoming data
    useEffect(() => {
        console.log('Widgets - Current metrics state:', {
            localHeaderMetrics,
            hasData: Object.keys(localHeaderMetrics).length > 0,
            rawData: localHeaderMetrics?.data,
            rawTrends: localHeaderMetrics?.data?.trends
        });
    }, [localHeaderMetrics]);

    // Listen for WebSocket updates
    useEffect(() => {
        const handleHeaderMetricsUpdate = (metrics) => {
            console.log('Widgets component received header metrics update:', metrics);
            if (metrics && Object.keys(metrics).length > 0) {
                setLocalHeaderMetrics(metrics);
                setLoading(false);
                setDataReceived(true);
            }
        };
        
        // Add event listener
        if (window.dashboardEvents) {
            console.log('Widgets - Setting up event listener for dashboard:headerMetrics');
            window.dashboardEvents.on('dashboard:headerMetrics', handleHeaderMetricsUpdate);
        } else {
            console.warn('Widgets - window.dashboardEvents is not available');
        }
        
        // Cleanup function
        return () => {
            if (window.dashboardEvents) {
                window.dashboardEvents.off('dashboard:headerMetrics', handleHeaderMetricsUpdate);
            }
        };
    }, []);

    // Set a timeout to stop showing loading spinner if data doesn't arrive
    useEffect(() => {
        const timer = setTimeout(() => {
            if (loading && !dataReceived) {
                setLoading(false);
            }
        }, 10000); // 10 seconds timeout
        
        return () => clearTimeout(timer);
    }, [loading, dataReceived]);

    // Helper function to safely parse numbers
    const safeParseInt = (value) => {
        if (value === undefined || value === null) return 0;
        const parsed = parseInt(value);
        return isNaN(parsed) ? 0 : parsed;
    };

    const safeParseFloat = (value) => {
        if (value === undefined || value === null) return 0;
        const parsed = parseFloat(value);
        return isNaN(parsed) ? 0 : parsed;
    };

    // Compute KPI data based on header metrics
    const kpiData = useMemo(() => {
        console.log('Widgets - Computing KPI data with:', localHeaderMetrics);

        // Extract data from the response
        const metrics = {
            total_active_users: safeParseInt(localHeaderMetrics?.total_active_users),
            new_users_today: safeParseInt(localHeaderMetrics?.new_users_today),
            active_subscriptions: safeParseInt(localHeaderMetrics?.active_subscriptions),
            total_revenue: safeParseFloat(localHeaderMetrics?.total_revenue),
            active_sessions: safeParseInt(localHeaderMetrics?.active_sessions),
            match_success_rate: safeParseFloat(localHeaderMetrics?.match_success_rate),
            trends: localHeaderMetrics?.trends || {}
        };

        console.log('Widgets - Processed metrics:', metrics);
        
        return [
            {
                id: 1,
                label: "Total Active Users",
                counter: metrics.total_active_users,
                decimals: 0,
                prefix: "",
                suffix: "",
                percentage: safeParseFloat(metrics.trends?.total_active_users || 0).toFixed(2),
                badgeClass: safeParseFloat(metrics.trends?.total_active_users || 0) >= 0 ? "success" : "danger",
                badge: safeParseFloat(metrics.trends?.total_active_users || 0) >= 0 ? "ri-arrow-right-up-line" : "ri-arrow-right-down-line",
                bgcolor: "primary",
                icon: "ri-team-line",
                link_url: "/users"
            },
            {
                id: 2,
                label: "New Users Today",
                counter: metrics.new_users_today,
                decimals: 0,
                prefix: "",
                suffix: "",
                percentage: safeParseFloat(metrics.trends?.new_users_today || 0).toFixed(2),
                badgeClass: safeParseFloat(metrics.trends?.new_users_today || 0) >= 0 ? "success" : "danger",
                badge: safeParseFloat(metrics.trends?.new_users_today || 0) >= 0 ? "ri-arrow-right-up-line" : "ri-arrow-right-down-line",
                bgcolor: "success",
                icon: "ri-user-add-line",
                link_url: "/users"
            },
            {
                id: 3,
                label: "Active Subscriptions",
                counter: metrics.active_subscriptions,
                decimals: 0,
                prefix: "",
                suffix: "",
                percentage: safeParseFloat(metrics.trends?.active_subscriptions || 0).toFixed(2),
                badgeClass: safeParseFloat(metrics.trends?.active_subscriptions || 0) >= 0 ? "success" : "danger",
                badge: safeParseFloat(metrics.trends?.active_subscriptions || 0) >= 0 ? "ri-arrow-right-up-line" : "ri-arrow-right-down-line",
                bgcolor: "info",
                icon: "ri-vip-crown-line",
                link_url: "/subscription-plans"
            },
            {
                id: 4,
                label: "Total Revenue",
                counter: metrics.total_revenue,
                decimals: 2,
                prefix: "$",
                suffix: "",
                percentage: safeParseFloat(metrics.trends?.total_revenue || 0).toFixed(2),
                badgeClass: safeParseFloat(metrics.trends?.total_revenue || 0) >= 0 ? "success" : "danger",
                badge: safeParseFloat(metrics.trends?.total_revenue || 0) >= 0 ? "ri-arrow-right-up-line" : "ri-arrow-right-down-line",
                bgcolor: "warning",
                icon: "ri-money-dollar-circle-line",
                link_url: "/revenue"
            },
            {
                id: 5,
                label: "Active Sessions",
                counter: metrics.active_sessions,
                decimals: 0,
                prefix: "",
                suffix: "",
                percentage: safeParseFloat(metrics.trends?.active_sessions || 0).toFixed(2),
                badgeClass: safeParseFloat(metrics.trends?.active_sessions || 0) >= 0 ? "success" : "danger",
                badge: safeParseFloat(metrics.trends?.active_sessions || 0) >= 0 ? "ri-arrow-right-up-line" : "ri-arrow-right-down-line",
                bgcolor: "danger",
                icon: "ri-pulse-line",
                link_url: "/sessions"
            },
            {
                id: 6,
                label: "Match Success Rate",
                counter: metrics.match_success_rate,
                decimals: 1,
                prefix: "",
                suffix: "%",
                percentage: safeParseFloat(metrics.trends?.match_success_rate || 0).toFixed(2),
                badgeClass: safeParseFloat(metrics.trends?.match_success_rate || 0) >= 0 ? "success" : "danger",
                badge: safeParseFloat(metrics.trends?.match_success_rate || 0) >= 0 ? "ri-arrow-right-up-line" : "ri-arrow-right-down-line",
                bgcolor: "secondary",
                icon: "ri-heart-2-line",
                link_url: "/matches"
            }
        ];
    }, [localHeaderMetrics]);

    // Debug log for rendered KPI data
    useEffect(() => {
        console.log('Rendered KPI data:', kpiData.map(item => ({
            label: item.label,
            counter: item.counter,
            percentage: item.percentage
        })));
    }, [kpiData]);

    return (
        <React.Fragment>
            {loading || isSampleHeaderMetrics(localHeaderMetrics) ? (
                <LogoSpinner message="Loading dashboard metrics..." size="medium" />
            ) : (
                kpiData.map((item, key) => (
                    <Col xl={2} md={4} key={key}>
                        <Card className="card-animate">
                            <CardBody>
                                <div className="text-center mb-3" style={{ minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <p className="text-uppercase fw-medium text-muted mb-0 w-100">
                                        {item.label}
                                    </p>
                                </div>
                                
                                <div className="d-flex align-items-center justify-content-center mb-4">
                                    <div className="avatar-sm">
                                        <span className={`avatar-title rounded fs-3 bg-${item.bgcolor}-subtle`}>
                                            <i className={`text-${item.bgcolor} ${item.icon}`}></i>
                                        </span>
                                    </div>
                                    <div className="ms-3">
                                        <h5 className={`fs-14 mb-0 text-${item.badgeClass}`}>
                                            {item.badge ? <i className={"fs-13 align-middle " + item.badge}></i> : null} 
                                            {item.percentage}%
                                        </h5>
                                    </div>
                                </div>

                                <div className="d-flex align-items-end justify-content-between mt-4">
                                    <div>
                                        <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                                            <span className="counter-value">
                                                <CountUp
                                                    start={0}
                                                    prefix={item.prefix}
                                                    suffix={item.suffix}
                                                    separator={","}
                                                    end={item.counter}
                                                    decimals={item.decimals}
                                                    duration={4}
                                                    preserveValue={true}
                                                />
                                            </span>
                                        </h4>
                                    </div>
                                    <Link 
                                        to={item.link_url} 
                                        className={`btn btn-sm btn-soft-${item.bgcolor} p-0 px-2`}
                                    >
                                        <i className="ri-arrow-right-line align-middle"></i>
                                    </Link>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                ))
            )}

            <style>
                {`
                    .card-animate .card-body {
                        padding: 1.3rem 1.3rem;
                    }
                    
                    .text-uppercase {
                        font-size: 0.85rem;
                        line-height: 1.2;
                    }

                    .smaller {
                        font-size: 0.75rem;
                    }
                `}
            </style>
        </React.Fragment>
    );
};

export default Widgets;